import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export type AiProvider = 'openai' | 'anthropic' | 'gemini' | 'xai'

/**
 * One agent definition. Scoped at one level — global, per-group, or
 * per-account. The runner picks the most specific agent that applies to
 * the action being prepared (account → group → global).
 */
export interface AiAgent {
  id: string
  /** Friendly name shown in the UI. */
  name: string
  /** When set, this agent ONLY applies to this account DID. */
  accountId?: string
  /** When set, this agent ONLY applies to this group id. */
  groupId?: string
  /** Otherwise: global default. */
  provider: AiProvider
  apiKey: string
  /** Model override (e.g. gpt-4o-mini). Empty string falls back to the
   *  provider default coded into the runner. */
  model: string
  /**
   * The user-authored prompt template. The runner injects context
   * variables (recent feed, top tags, account purpose, scheduled queue
   * snapshot, etc) before sending. The agent MUST respond with strict
   * JSON matching the AGENT_RESPONSE_SCHEMA below — no prose, no
   * markdown.
   */
  systemPrompt: string
  /** Cron-like cadence in minutes (how often the agent autonomously
   *  proposes new content). 0 = manual only. */
  cadenceMin: number
  enabled: boolean
  createdAt: number
  updatedAt: number
}

/**
 * The JSON schema we tell every AI agent it must conform to. Stored as
 * a string literal here so we can reference it from the prompt and from
 * the response validator.
 */
export const AGENT_RESPONSE_SCHEMA = `{
  "actions": [
    {
      "kind": "post" | "reply" | "like" | "repost" | "follow" | "unfollow",
      "targetUri": "<at-uri or did, depending on kind>",
      "targetCid": "<cid for like/repost; null otherwise>",
      "text": "<post text — for post/reply only; <=300 graphemes>",
      "mediaIds": ["<bskyhq-media-id>", ...],
      "runAtIso": "<ISO 8601 timestamp; null = run now>",
      "rationale": "<one short sentence why this action>"
    }
  ],
  "engagementHints": {
    "topTags": ["<#tag>", ...],
    "noteForUser": "<one short paragraph the agent wants the operator to read>"
  }
}`

interface AiState {
  agents: AiAgent[]
  upsert: (a: AiAgent) => void
  remove: (id: string) => void
  toggle: (id: string) => void
  reset: () => void
}

export const useAiSettings = create<AiState>()(
  persist(
    (set) => ({
      agents: [],
      upsert: (a) =>
        set((s) => {
          const exists = s.agents.some((x) => x.id === a.id)
          return {
            agents: exists
              ? s.agents.map((x) => (x.id === a.id ? { ...a, updatedAt: Date.now() } : x))
              : [...s.agents, a],
          }
        }),
      remove: (id) => set((s) => ({ agents: s.agents.filter((a) => a.id !== id) })),
      toggle: (id) =>
        set((s) => ({
          agents: s.agents.map((a) =>
            a.id === id ? { ...a, enabled: !a.enabled, updatedAt: Date.now() } : a,
          ),
        })),
      reset: () => set({ agents: [] }),
    }),
    { name: 'bskyhq:ai', version: 1 },
  ),
)

/**
 * Pick the most-specific applicable agent for an account.
 * Ordering: per-account > per-group > global. Disabled agents are skipped.
 */
export function pickAgent(opts: {
  accountId?: string
  groupId?: string
}): AiAgent | null {
  const agents = useAiSettings.getState().agents.filter((a) => a.enabled)
  if (opts.accountId) {
    const a = agents.find((x) => x.accountId === opts.accountId)
    if (a) return a
  }
  if (opts.groupId) {
    const g = agents.find((x) => x.groupId === opts.groupId && !x.accountId)
    if (g) return g
  }
  return agents.find((x) => !x.accountId && !x.groupId) ?? null
}

export function newAgentId(): string {
  return `ag_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`
}
